<script lang="ts">
	import Map from "./components/map/Map.svelte";
	import RoutesLayer from "./components/map/layers/RoutesLayer.svelte";
	import LocationsLayer from "./components/map/layers/locations/LocationsLayer.svelte";
	import type { Location } from "./components/data/Location";
	import { MapHook } from "./components/map/MapHook";
	import Routing from "./components/data/Routing.svelte";
	import type { RouteData } from "./components/data/RouteData";
	import ImageryLayer from "./components/map/layers/ImageryLayer.svelte";
	import type { RoutesLayerHook } from "./components/map/layers/RoutesLayerHook";
	import type { LocationsLayerHook } from "./components/map/layers/locations/LocationsLayerHook";
	import type { UserLocationLayerHook } from "./components/map/layers/UserLocationLayerHook";
	import UserLocationLayer from "./components/map/layers/UserLocationLayer.svelte";
	import { afterUpdate, onMount } from "svelte";
	import type { AttributionControlOptions } from "./components/map/controls/attribution/AttributionControlOptions";
	import type { BaseLayerControlOptions } from "./components/map/controls/baselayer/BaseLayerControlOptions";
	import BaseLayerControl from "./components/map/controls/baselayer/BaseLayerControl.svelte";
	import AttributionControl from "./components/map/controls/attribution/AttributionControl.svelte";
	import * as turf from "@turf/turf";
	import type { LngLatLike } from "maplibre-gl";
    import Head from "../header/Head.svelte";
	import { navigate } from "svelte-navigator";
	import LanguageSelector from "./LanguageSelector.svelte";
	import { appManager } from "../AppManager";
    import type { ReRouteDetailsData } from "../api/projects/data/reroute/ReRouteDetailsData";
    import type { ProjectData } from "../api/projects/data/projects/ProjectData";
    import type { ScenarioData } from "../api/projects/data/scenarios/ScenarioData";

	// exports.
	export let id: string = "";

	let projectData: ProjectData;
	let reRouteData: ReRouteDetailsData;
	let scenario0: ScenarioData;
	let scenario1: ScenarioData;
	let center: LngLatLike;

	$: if (typeof projectData === "undefined" && id.length > 0) {
		loadReRouteData().then();
	}

	const loadReRouteData = async () => {
		if (await appManager.isSignedIn()) {
			reRouteData = await appManager.api.projects.getReRoute({
				accessToken:
					await appManager.api.getAccessToken(),
				id: id,
			});
		} else {
			reRouteData = await appManager.api.projects.getReRoute({
				id: id,
			});
		}

		if (typeof reRouteData === "undefined") {
			navigate("/notfound");
			return;
		}

		projectData = reRouteData.project;

		// the scenario area.
		let area = projectData.area;

		// initialize the map.
		let zoom: number = 17;
		let bbox = turf.bbox(area);
		center = turf.center(area).geometry.coordinates as LngLatLike;

		scenario1 = reRouteData.scenarios[0]?.scenario;
		scenario0 = reRouteData.scenarios[1]?.scenario;

		if (typeof scenario0 === "undefined") {
			scenario0 = scenario1; // in case there is only one scenario.
			scenario1 = undefined;
		}
	};

	let dataElement: HTMLElement;
	let mapElement: HTMLElement;
	let heights: {
		data: string;
		map: string;
	} = {
		data: "25%",
		map: "calc(75% + 6px)",
	};

	onMount(async () => {
		dataElement = document.getElementById("data");
		mapElement = document.getElementById("map");
	});

	let settingsOpen: boolean = false;

	let mapHook: MapHook = new MapHook();
	let routingLayerHook: RoutesLayerHook;
	let locationsLayerHook: LocationsLayerHook;
	let userLocationLayerHook: UserLocationLayerHook;

	let attributionOptions: AttributionControlOptions = {
		customAttribution: '<a href="https://www.anyways.eu/">ANYWAYS BV</a>',
		editButton: undefined,
	};
	let baseLayerOptions: BaseLayerControlOptions = {
		source: "aiv",
		images: {
			imagery: "img/base-layers/sattelite.png",
			map: "img/base-layers/map.png",
		},
		keepOriginal: [
			"edges-selected-highway-construction",
			"cyclestreets",
			"detour",
			"edges-selected-highway-construction-black",
		],
	};
	let profiles: {
		id: string;
		description: string;
		icon: string;
		longDescription: string;
	}[] = [
		{
			id: "car.fast",
			description: "reroute.vehicle.car.name",
			icon: "car",
			longDescription: "reroute.vehicle.car.description",
		},
		{
			id: "bicycle.commute",
			description: "reroute.vehicle.bicycle.name",
			icon: "bicycle",
			longDescription: "reroute.vehicle.bicycle.description",
		},
	];

	function onExpand(expand: boolean) {
		if (expand) {
			heights = {
				data: "calc(75% + 6px)",
				map: "25%",
			};
		} else {
			heights = {
				data: "25%",
				map: "calc(75% + 6px)",
			};
		}
	}

	let profile: string = "car.fast";
	let locations: Location[] = [
		{
			id: 0,
		},
		{
			id: 1,
		},
	];
	let routes: RouteData[] = [];

	let dragState: {
		height?: number;
		dragging?: {
			startTouch: number;
			startHeight: number;
		};
	} = {};

	function onTouchStart(e: any) {
		dragState.height = dataElement.clientHeight;
		dragState.dragging = {
			startTouch: e.touches[0].clientY,
			startHeight: dragState.height,
		};
	}

	function onTouchMove(e: any) {
		dragState.height =
			dragState.dragging.startHeight -
			(e.touches[0].clientY - dragState.dragging.startTouch);

		heights.data = dragState.height + "px";
		heights.map = "calc(100% - " + dragState.height + "px + 6px)";
		dataElement.style.height = heights.data;
		mapElement.style.height = heights.map;
		mapHook.resize();
	}

	function onTouchEnd(e: any) {}

	afterUpdate(() => {
		mapHook.resize();
	});
</script>

<Head />
{#if typeof scenario0 !== "undefined"}
	<div id="full" class="full">
		<div id="map" class="map" style="height: {heights.map};">
			<Map bind:hook={mapHook} {center} scenario1={scenario0}>
				<RoutesLayer {routes} bind:routeLayerHook={routingLayerHook} />
				<LocationsLayer {locations} bind:locationsLayerHook />
				<ImageryLayer />
				<UserLocationLayer bind:hook={userLocationLayerHook} />

				<AttributionControl options={attributionOptions} />
				<BaseLayerControl bind:options={baseLayerOptions} />
			</Map>
		</div>

		<div class="bottom-left">
			<LanguageSelector />
		</div>

		<div
			id="data"
			class="data {settingsOpen ? 'd-none' : ''}"
			style="height: {heights.data};"
			on:touchstart={onTouchStart}
			on:touchmove={onTouchMove}
			on:touchend={onTouchEnd}>
			<Routing
				bind:routeLayerHook={routingLayerHook}
				{profiles}
				bind:mapHook
				bind:routes
				bind:locations
				bind:profile
				bind:locationsLayerHook
				bind:userLocationLayerHook
				{center}
				{scenario0}
				{scenario1}
				project={projectData}
				on:expand={(e) => onExpand(e.detail)} />
		</div>
	</div>
{/if}

<style>
	.full {
		position: absolute;
		top: 0px;
		bottom: 0px;
		left: 0px;
		right: 0px;
		font: 16px/20px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
			Helvetica Neue, Arial, Noto Sans, Liberation Sans, sans-serif,
			Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
		font-weight: 400;
		line-height: 1.5;
	}

	.map {
		position: absolute;
		top: 0;
		bottom: 0;
		width: 100%;
		min-height: calc(25% + 6px);
		max-height: calc(100% - 195px + 6px);
	}

	.data {
		position: absolute;
		bottom: 0px;
		left: 0px;
		right: 0px;
		border-top-left-radius: 10px;
		border-top-right-radius: 10px;
		min-height: 195px;
		max-height: 75%;
	}

	@media (min-width: 576px) {
		.full {
			font-size: 12px;
		}

		.map {
			position: absolute;
			top: 0px;
			left: 0px;
			right: 0px;
			min-height: 100%;
			max-height: 100%;
		}

		.data {
			position: absolute;
			left: 10px;
			top: 20px;
			border-top-left-radius: 10px;
			border-top-right-radius: 10px;
			width: 310px;
			min-height: 0px;
			max-height: 0px;
		}

		.bottom-left {
			position: absolute;
			left: 10px;
			bottom: 10px;
		}
	}
</style>
